import { MparticleSegmentReadiness } from './types';

/**
 * How many consecutive failed ingestion attempts before a segment is reported as stuck.
 * Ingestion retries daily, so this is "three days without valid data" — long enough not to
 * alarm on a single transient failure, short enough that nobody lives with it unnoticed.
 */
export const STUCK_AFTER_FAILURES = 3;

/**
 * What the UI shows, which is NOT what the backend reports. The backend only distinguishes
 * `synchronizing` from `ready`; `stuck` and `stale` are refinements derived from
 * consecutiveFailures and the staleness flag.
 */
export type SegmentDisplayStatus = 'ready' | 'stale' | 'stuck' | 'synchronizing';

/**
 * Single source of truth for the derivation. Both the segments list and the offer's segment
 * dropdown must import this: if either derived the status on its own they would eventually
 * disagree, and the list would report a segment as synchronizing while the dropdown offered
 * it as usable.
 */
export const getSegmentDisplayStatus = (readiness?: MparticleSegmentReadiness): SegmentDisplayStatus => {
  if (!readiness) {
    return 'synchronizing';
  }

  if (readiness.syncStatus === 'ready') {
    return readiness.stale ? 'stale' : 'ready';
  }

  // `escalated` is the backend's own threshold verdict; the local count is a fallback in case
  // an older backend omits the flag. Either signal marks the segment as stuck.
  const isStuck = readiness.escalated || readiness.consecutiveFailures >= STUCK_AFTER_FAILURES;

  return isStuck ? 'stuck' : 'synchronizing';
};

/**
 * Whether a segment can back an offer. `stale` counts as usable on purpose: the membership is
 * outdated but still present, so the offer reaches real customers. Only an empty membership
 * makes an offer reach nobody.
 */
export const isSegmentUsable = (status: SegmentDisplayStatus): boolean => status === 'ready' || status === 'stale';
